import { LitElement, html, property } from 'lit-element'
import { register } from '@polleverywhere/hmr-custom-element'
import { bemBlock } from '@polleverywhere/bem-helpers'
import { defineElementSlot } from '@polleverywhere/element-helpers'
import { contextualize } from '@polleverywhere/cutlass'
import { createPopper } from '@popperjs/core'
import './pe-menu-item'
import './pe-menu.scss'

/**
 * Renders a menu with menu items that a user must choose from. Each menu item
 * may be rendered with enabled/disabled states and with an optional icon. The
 * menu can be positioned and aligned differently as needed.
 *
 * ```
 * // Aligned to the top edge and centered.
 * <pe-menu position='top'>
 *   <pe-menu-item name='add' icon='plus'>Add</pe-menu-item>
 *   <pe-menu-item name='delete' icon='close'>Delete</pe-menu-item>
 * </pe-menu>
 * ```
 *
 * An optional control slot may be rendered acting as the trigger to open or
 * close the menu.
 *
 * ```
 * // Hovering over control opens the menu
 * <pe-menu trigger='hover'>
 *   <a slot='control'>Hover me</a>
 *   <pe-menu-item name='add' icon='plus'>Add</pe-menu-item>
 *   <pe-menu-item name='delete' icon='close'>Delete</pe-menu-item>
 * </pe-menu>
 * ```
 *
 * Menu dividers can are inserted with `<hr>` elements to create sections.
 *
 * ```
 * // Use dividers between menu items
 * <pe-menu>
 *   <pe-menu-item name='add' icon='plus'>Add</pe-menu-item>
 *   <hr>
 *   <pe-menu-item name='delete' icon='close'>Delete</pe-menu-item>
 * </pe-menu>
 * ```
 *
 * @element pe-menu
 *
 * @slot - Element containing the list of menu items
 * @slot control - Control that lets users open/close the menu
 *
 * @prop {boolean} open - Open or close state
 * @prop {string} position=bottom-start - Position value from `Menu.positions`
 *  that determines how to position the menu: `top`, `top-start`, `top-end`,
 *  `bottom`, `bottom-start`, `bottom-end`
 * @prop {number} offsetSkidding=0 - Amount (in pixels) to offset the menu
 *  laterally along the edge of the control, starting from `position`
 * @prop {number} offsetDistance=0 - Amount (in pixels) to offset the menu away
 *  from the edge of the control
 * @prop {string} trigger=click - Mechanism by which to open/close the menu and
 *  is only applicable when "control" slot is provided: 'click', 'hover'
 *
 * @fires {Object} action - Fires when menu item is selected
 * @fires {Object} open - Fires whenever menu is opened
 * @fires {Object} close - Fires whenever menu is closed
 *
 * @cssprop --pe-menu-background=$pe-pure-white - Menu background
 */
export default class Menu extends LitElement {
  static tagName = 'pe-menu'

  static positions = {
    top: 'top',
    topStart: 'top-start',
    topEnd: 'top-end',
    bottom: 'bottom',
    bottomStart: 'bottom-start',
    bottomEnd: 'bottom-end'
  }

  static triggers = {
    click: 'click',
    hover: 'hover'
  }

  @property({ type: Boolean, reflect: true }) open = false

  @property({ type: String }) position = Menu.positions.bottomStart

  @property({ type: Number, attribute: 'offset-skidding' })
  offsetSkidding = 0

  @property({ type: Number, attribute: 'offset-distance' })
  offsetDistance = 0

  @property({ type: String }) trigger = Menu.triggers.click

  get open () {
    return this.__open
  }

  set open (value) {
    const oldValue = this.open
    if (value === oldValue) return

    this.__open = value
    this.requestUpdate('open', oldValue)

    // When menu is opened, enable listeners to auto-close the menu on click.
    toggleAutoCloser.call(this, this.__open)

    const eventType = this.__open ? 'open' : 'close'
    this.dispatchEvent(new CustomEvent(eventType, {
      bubbles: true,
      detail: {}
    }))
  }

  constructor () {
    super()

    // contextualize cutlass events
    contextualize(this, this.tagName)
  }

  connectedCallback () {
    super.connectedCallback()
    defineElementSlot(this)
    defineElementSlot(this, 'control')
  }

  createRenderRoot () { return this }

  render () {
    this.updateComplete.then(updatePopper.bind(this))

    return html`<div
      @mouseleave=${handleTrigger}
      class=${bem('root')}>
      ${controlTemplate.call(this)}
      ${menuTemplate.call(this)}
    </div>`
  }
}

function updatePopper () {
  this.popperInstance = this.popperInstance || createPopper(
    this.querySelector(`.${bem('control')}`) || this,
    this.querySelector(`.${bem('menu-wrapper')}`))

  const validPositionValues = Object.values(Menu.positions)
  if (!validPositionValues.includes(this.position)) {
    throw new Error(`Position must be one of: ${validPositionValues}`)
  }

  this.popperInstance.setOptions({
    placement: this.position,
    modifiers: [{
      name: 'offset',
      options: { offset: [this.offsetSkidding, this.offsetDistance] }
    }]
  })
}

function toggleAutoCloser (enable, e) {
  if (!enable) {
    document.removeEventListener('mousedown', this._handleAutoCloser, true)
    this._handleAutoCloser = undefined
    return
  }

  this._handleAutoCloser = (e) => {
    // Don't auto-close if actions occurred within this menu.
    if (this === e.target || this === e.target.closest('pe-menu')) return

    this.open = false
  }
  document.addEventListener('mousedown', this._handleAutoCloser, true)
}

function controlTemplate () {
  if (!this.slots('control')[0]) return ''

  return html`<div
    @mouseenter=${handleTrigger}
    @click=${handleTrigger}
    aria-expanded=${this.open}
    aria-haspopup='true'
    class=${bem('control')}
  >${this.slots('control')[0]}</div>`
}

function menuTemplate () {
  this.querySelectorAll('hr')
    .forEach(el => {
      el.classList.add(...bem('separator').split(' '))
      el.setAttribute('role', 'separator')
    })

  return html`<div class=${bem('menu-wrapper')}>
    <span @action=${handleAction}
        aria-hidden=${!this.open}
        aria-orientation='vertical'
        class=${bem('menu')}
        role='menu'>
      ${this.slots()}
    </span>
  </div>`
}

function handleAction (e) {
  this.open = false
}

function handleTrigger (e) {
  if (!getValidTriggerEvents(this.trigger).includes(e.type)) return

  e.stopPropagation()
  this.open = e.type.includes('mouse') ? e.type === 'mouseenter' : !this.open
}

function getValidTriggerEvents (trigger) {
  const eventsByType = {
    [Menu.triggers.click]: ['click'],
    [Menu.triggers.hover]: ['mouseenter', 'mouseleave']
  }
  return eventsByType[trigger]
}

const bem = bemBlock(Menu.tagName)
register(module, Menu.tagName, Menu)
